
import React, { useState } from 'react';
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Button } from "@/components/ui/button";
import { Loader2, Upload, XCircle } from "lucide-react";
import { format } from "date-fns";
import { UserProfile } from "./useUsersAdmin";

interface UserProfileFormProps {
  user: UserProfile;
  onUpdateProfile: (data: Partial<UserProfile>) => Promise<boolean>;
  onUploadFile: (file: File, userId: string, fileType: 'license' | 'logo') => Promise<string | null>;
  isUploading: boolean;
}

export function UserProfileForm({ user, onUpdateProfile, onUploadFile, isUploading }: UserProfileFormProps) {
  const [formData, setFormData] = useState<Partial<UserProfile>>({
    firstName: user.firstName || user.first_name || '',
    lastName: user.lastName || user.last_name || '',
    email: user.email || '',
    phone: user.phone || '',
    companyName: user.companyName || user.company_name || '',
    abnAcn: user.abnAcn || user.abn_acn || '',
    companyAddress: user.companyAddress || user.company_address || '',
    stateTerritory: user.stateTerritory || user.state_territory || '',
    licenseType: user.licenseType || user.license_type || '',
    licenseNumber: user.licenseNumber || user.license_number || '',
    licenseExpiry: user.licenseExpiry || user.license_expiry || '',
    licenseDocumentUrl: user.licenseDocumentUrl || user.license_document_url || '',
    companyLogoUrl: user.companyLogoUrl || user.company_logo_url || '',
    websiteUrl: user.websiteUrl || user.website_url || '',
    companyDescription: user.companyDescription || user.company_description || '',
    operationalStatus: user.operationalStatus || user.operational_status || 'Active',
    registrationDate: user.registrationDate || user.registration_date || '',
    role: user.role || 'customer',
    termsAgreement: user.termsAgreement || user.terms_agreement || false,
  });

  const [licenseFile, setLicenseFile] = useState<File | null>(null);
  const [logoFile, setLogoFile] = useState<File | null>(null);
  const [isSaving, setIsSaving] = useState(false);

  const handleChange = (field: string, value: any) => {
    setFormData({ ...formData, [field]: value });
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>, fileType: 'license' | 'logo') => {
    if (e.target.files && e.target.files[0]) {
      if (fileType === 'license') {
        setLicenseFile(e.target.files[0]);
      } else {
        setLogoFile(e.target.files[0]);
      }
    }
  };

  const handleSubmit = async () => {
    setIsSaving(true);
    try {
      // Handle file uploads first
      let updatedData = { ...formData };
      
      if (licenseFile) {
        const licenseUrl = await onUploadFile(licenseFile, user.id, 'license');
        if (licenseUrl) {
          updatedData.licenseDocumentUrl = licenseUrl;
        }
      }
      
      if (logoFile) {
        const logoUrl = await onUploadFile(logoFile, user.id, 'logo');
        if (logoUrl) {
          updatedData.companyLogoUrl = logoUrl;
        }
      }
      
      // Then update the profile
      await onUpdateProfile(updatedData);
      
      // Reset file states after successful update
      setLicenseFile(null);
      setLogoFile(null);
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="space-y-2">
          <Label htmlFor="firstName">First Name</Label>
          <Input 
            id="firstName" 
            value={formData.firstName || ''} 
            onChange={(e) => handleChange('firstName', e.target.value)}
          />
        </div>
        
        <div className="space-y-2">
          <Label htmlFor="lastName">Last Name</Label>
          <Input 
            id="lastName" 
            value={formData.lastName || ''} 
            onChange={(e) => handleChange('lastName', e.target.value)}
          />
        </div>
      </div>
      
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="space-y-2">
          <Label htmlFor="email">Email Address</Label>
          <Input 
            id="email" 
            type="email" 
            value={formData.email || ''} 
            onChange={(e) => handleChange('email', e.target.value)}
          />
        </div>
        
        <div className="space-y-2">
          <Label htmlFor="phone">Phone Number</Label>
          <Input 
            id="phone" 
            value={formData.phone || ''} 
            onChange={(e) => handleChange('phone', e.target.value)}
          />
        </div>
      </div>
      
      <div className="space-y-2">
        <Label htmlFor="companyName">Company Name</Label>
        <Input 
          id="companyName" 
          value={formData.companyName || ''} 
          onChange={(e) => handleChange('companyName', e.target.value)}
        />
      </div>
      
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="space-y-2">
          <Label htmlFor="abnAcn">ABN/ACN</Label>
          <Input 
            id="abnAcn" 
            value={formData.abnAcn || ''} 
            onChange={(e) => handleChange('abnAcn', e.target.value)}
          />
        </div>
        
        <div className="space-y-2">
          <Label htmlFor="operationalStatus">Operational Status</Label>
          <Select 
            value={formData.operationalStatus || 'Active'} 
            onValueChange={(value) => handleChange('operationalStatus', value)}
          >
            <SelectTrigger id="operationalStatus">
              <SelectValue placeholder="Select status" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="Active">Active</SelectItem>
              <SelectItem value="Inactive">Inactive</SelectItem>
              <SelectItem value="Pending">Pending</SelectItem>
              <SelectItem value="Suspended">Suspended</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>
      
      <div className="space-y-2">
        <Label htmlFor="companyAddress">Company Address</Label>
        <Input 
          id="companyAddress" 
          value={formData.companyAddress || ''} 
          onChange={(e) => handleChange('companyAddress', e.target.value)}
        />
      </div>
      
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="space-y-2">
          <Label htmlFor="stateTerritory">State/Territory</Label>
          <Select 
            value={formData.stateTerritory || ''} 
            onValueChange={(value) => handleChange('stateTerritory', value)}
          >
            <SelectTrigger id="stateTerritory">
              <SelectValue placeholder="Select state/territory" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="ACT">Australian Capital Territory</SelectItem>
              <SelectItem value="NSW">New South Wales</SelectItem>
              <SelectItem value="NT">Northern Territory</SelectItem>
              <SelectItem value="QLD">Queensland</SelectItem>
              <SelectItem value="SA">South Australia</SelectItem>
              <SelectItem value="TAS">Tasmania</SelectItem>
              <SelectItem value="VIC">Victoria</SelectItem>
              <SelectItem value="WA">Western Australia</SelectItem>
            </SelectContent>
          </Select>
        </div>
        
        <div className="space-y-2">
          <Label htmlFor="registrationDate">Registration Date</Label>
          <Input 
            id="registrationDate" 
            type="date" 
            value={formData.registrationDate ? formData.registrationDate.substring(0, 10) : ''}
            onChange={(e) => handleChange('registrationDate', e.target.value)}
          />
        </div>
      </div>
      
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="space-y-2">
          <Label htmlFor="licenseType">License Type</Label>
          <Select 
            value={formData.licenseType || ''} 
            onValueChange={(value) => handleChange('licenseType', value)}
          >
            <SelectTrigger id="licenseType">
              <SelectValue placeholder="Select license type" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="Cultivator">Cultivator</SelectItem>
              <SelectItem value="Manufacturer">Manufacturer</SelectItem>
              <SelectItem value="Research">Research</SelectItem>
              <SelectItem value="Distributor">Distributor</SelectItem>
              <SelectItem value="Retailer">Retailer</SelectItem>
              <SelectItem value="Other">Other</SelectItem>
            </SelectContent>
          </Select>
        </div>
        
        <div className="space-y-2">
          <Label htmlFor="licenseNumber">License Number</Label>
          <Input 
            id="licenseNumber" 
            value={formData.licenseNumber || ''} 
            onChange={(e) => handleChange('licenseNumber', e.target.value)}
          />
        </div>
      </div>
      
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="space-y-2">
          <Label htmlFor="licenseExpiry">License Expiry Date</Label>
          <Input 
            id="licenseExpiry" 
            type="date" 
            value={formData.licenseExpiry ? formData.licenseExpiry.substring(0, 10) : ''}
            onChange={(e) => handleChange('licenseExpiry', e.target.value)}
          />
        </div>
        
        <div className="space-y-2">
          <Label htmlFor="role">User Role</Label>
          <Select 
            value={formData.role || 'customer'} 
            onValueChange={(value) => handleChange('role', value)}
          >
            <SelectTrigger id="role">
              <SelectValue placeholder="Select role" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="admin">Admin</SelectItem>
              <SelectItem value="customer">Customer</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>
      
      <div className="space-y-2">
        <Label htmlFor="licenseDocument">License Document</Label>
        <div className="flex items-center gap-4">
          {formData.licenseDocumentUrl && (
            <div className="relative h-16 w-16 border rounded overflow-hidden">
              <a 
                href={formData.licenseDocumentUrl} 
                target="_blank" 
                rel="noopener noreferrer"
                className="block h-full w-full bg-muted flex items-center justify-center"
              >
                View
              </a>
              <Button
                variant="outline"
                size="icon"
                className="absolute top-0 right-0 h-6 w-6 rounded-full"
                onClick={() => handleChange('licenseDocumentUrl', '')}
              >
                <XCircle className="h-4 w-4" />
              </Button>
            </div>
          )}
          <div className="flex-1">
            <Input
              id="licenseDocument"
              type="file"
              onChange={(e) => handleFileChange(e, 'license')}
              className="cursor-pointer"
            />
            {licenseFile && <p className="text-sm text-muted-foreground mt-1">{licenseFile.name}</p>}
          </div>
        </div>
      </div>
      
      <div className="space-y-2">
        <Label htmlFor="companyLogo">Company Logo</Label>
        <div className="flex items-center gap-4">
          {formData.companyLogoUrl && (
            <div className="relative h-16 w-16 border rounded overflow-hidden">
              <img 
                src={formData.companyLogoUrl} 
                alt="Company logo" 
                className="h-full w-full object-cover"
              />
              <Button
                variant="outline"
                size="icon"
                className="absolute top-0 right-0 h-6 w-6 rounded-full"
                onClick={() => handleChange('companyLogoUrl', '')}
              >
                <XCircle className="h-4 w-4" />
              </Button>
            </div>
          )}
          <div className="flex-1">
            <Input
              id="companyLogo"
              type="file"
              accept="image/*"
              onChange={(e) => handleFileChange(e, 'logo')}
              className="cursor-pointer"
            />
            {logoFile && <p className="text-sm text-muted-foreground mt-1">{logoFile.name}</p>}
          </div>
        </div>
      </div>
      
      <div className="space-y-2">
        <Label htmlFor="websiteUrl">Website URL</Label>
        <Input 
          id="websiteUrl" 
          type="url"
          value={formData.websiteUrl || ''} 
          onChange={(e) => handleChange('websiteUrl', e.target.value)}
        />
      </div>
      
      <div className="space-y-2">
        <Label htmlFor="companyDescription">Company Description</Label>
        <Textarea 
          id="companyDescription" 
          rows={4}
          value={formData.companyDescription || ''} 
          onChange={(e) => handleChange('companyDescription', e.target.value)}
        />
      </div>
      
      <div className="flex items-center space-x-2">
        <Switch
          id="termsAgreement"
          checked={!!formData.termsAgreement}
          onCheckedChange={(value) => handleChange('termsAgreement', value)}
        />
        <Label htmlFor="termsAgreement">Terms and Conditions Agreement</Label>
      </div>
      
      <div className="pt-4">
        <Button 
          type="button" 
          className="w-full" 
          onClick={handleSubmit}
          disabled={isSaving || isUploading}
        >
          {(isSaving || isUploading) && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
          Save Changes
        </Button>
      </div>
    </div>
  );
}
